import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { RiAddLine, RiDeleteBinLine, RiSendPlane2Fill } from "@remixicon/react";
import { getBookingReminders, sendTestBookingReminder, setBookingReminders } from "../../services/bookingReminders";
import { Card, Field, FieldLabel, SectionHead } from "../Notifications/ComposeParts";
import { ErrorState } from "../../components/kit/States";
import { srOnly, tokens } from "../../utils/figmaTokens";
import {
  GROUPS,
  HOURS,
  LIMITS,
  MESSAGE_LABELS,
  dayList,
  firstProblem,
  hourLabel,
  lastRunView,
  ordinal,
  toNumber,
} from "../../utils/bookingRemindersForm";

// Growth → Booking reminders (spec 055 D20–D26): the automatic reminders to students who stopped
// booking — on or off, the send time, each group's steps and messages, a test send, and the last
// run's numbers. Built from the Notifications page's parts (D26). The server checks every save
// again (functions/bookingReminders.js) and writes the activity-log row itself.

const MESSAGE_KEYS = GROUPS.flatMap((g) => g.messages.map((m) => m.key));

const Line = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>{children}</Box>
);

// A number field: empty shows empty and reads as NaN, which the checks refuse (never as 0).
const NumberField = ({ value, onChange, label, min }) => (
  <Field
    type="number"
    value={Number.isNaN(value) ? "" : value}
    onChange={(e) => onChange(toNumber(e.target.value))}
    inputProps={{ min, max: LIMITS.gap, "aria-label": label }}
    sx={{ width: 96 }}
  />
);

const SwitchLine = ({ id, checked, onChange, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
    <Switch id={id} checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <Typography component="label" htmlFor={id} variant="bodySmall" sx={{ color: tokens.text.primary }}>
      {label}
    </Typography>
  </Box>
);

const BookingReminders = () => {
  const [loaded, setLoaded] = useState(null);
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [confirmLive, setConfirmLive] = useState(false);
  const [toast, setToast] = useState("");
  const [test, setTest] = useState({ uid: "", key: MESSAGE_KEYS[0], sending: false, error: "" });

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const data = await getBookingReminders();
      setLoaded(data);
      setForm(data.settings);
      setTest((t) => ({ ...t, uid: t.uid || (data.testAccounts[0] && data.testAccounts[0].uid) || "" }));
    } catch (e) {
      setLoadError(e.message || "Try again in a moment.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const problem = useMemo(() => (form ? firstProblem(form) : null), [form]);
  useEffect(() => setSaveError(""), [form]);

  if (loadError) return <ErrorState title="Could not load the booking reminders" message={loadError} onRetry={load} />;
  if (!form) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress aria-label="Loading the booking reminders" />
      </Box>
    );
  }

  const dirty = JSON.stringify(form) !== JSON.stringify(loaded.settings);
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const patchGroup = (key, p) => setForm((f) => ({ ...f, groups: { ...f.groups, [key]: { ...f.groups[key], ...p } } }));
  const patchStep = (key, i, value) =>
    setForm((f) => {
      const steps = [...f.groups[key].steps];
      steps[i] = value;
      return { ...f, groups: { ...f.groups, [key]: { ...f.groups[key], steps } } };
    });
  const patchMessage = (key, p) =>
    setForm((f) => ({ ...f, messages: { ...f.messages, [key]: { ...f.messages[key], ...p } } }));

  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      await setBookingReminders({ settings: form });
      setLoaded((l) => ({ ...l, settings: form }));
      setToast("Saved");
    } catch (e) {
      setSaveError(e.message || "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };
  // D19: switching sending on — or changing the schedule while it is on — sends each due
  // reminder to everyone already past its day, all at once, at the next send time.
  const startsSending = !loaded.settings.live;
  const wave = form.live && (startsSending || JSON.stringify(form.groups) !== JSON.stringify(loaded.settings.groups));
  const onSave = () => (wave ? setConfirmLive(true) : save());

  const sendTest = async () => {
    const m = form.messages[test.key];
    setTest((t) => ({ ...t, sending: true, error: "" }));
    try {
      const out = await sendTestBookingReminder({ uid: test.uid, key: test.key, title: m.title, body: m.body });
      setToast(`Test sent to ${out.devices} ${out.devices === 1 ? "device" : "devices"}`);
      setTest((t) => ({ ...t, sending: false }));
    } catch (e) {
      setTest((t) => ({ ...t, sending: false, error: e.message || "Could not send the test." }));
    }
  };

  const run = lastRunView(loaded.lastRun);
  const lastLine = !run ? "" : loaded.lastRun.live ? ` The last run sent ${run.total}.` : ` The last count was ${run.total}.`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", maxWidth: 880 }}>
      <Typography component="h1" sx={srOnly}>
        Booking reminders
      </Typography>

      <Card>
        <SectionHead large title="Booking reminders" subtitle="A push to students who stopped booking, once a day. Booking a lesson stops them." />
        <SwitchLine id="br-live" checked={form.live} onChange={(live) => patch({ live })} label="Send reminders" />
        <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
          {form.live
            ? "On: students who are due get a reminder at the send time."
            : "Off: the server only counts who would get one. Nothing is sent."}
        </Typography>
        <TextField
          select
          size="small"
          id="br-hour"
          label="Send time (UK time)"
          value={form.hour}
          onChange={(e) => patch({ hour: Number(e.target.value) })}
          slotProps={{ select: { native: true } }}
          sx={{ maxWidth: 220 }}
        >
          {HOURS.map((h) => (
            <option key={h} value={h}>
              {hourLabel(h)}
            </option>
          ))}
        </TextField>
        <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
          Messages can use {"{name}"} (first name), {"{days}"} ("7 days", or "a while" after 30 days) and {"{credits}"} ("3 credits").
        </Typography>
      </Card>

      {GROUPS.map((g) => {
        const v = form.groups[g.key];
        return (
          <Card key={g.key}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
              <SectionHead title={g.label} subtitle={g.help} />
              <SwitchLine id={`br-${g.key}-on`} checked={v.on} onChange={(on) => patchGroup(g.key, { on })} label={`${g.label}: ${v.on ? "on" : "off"}`} />
            </Box>
            {v.steps.map((gap, i) => (
              <Line key={i}>
                <Typography variant="bodySmall" sx={{ width: 150 }}>
                  {i === 0 ? "1st reminder after" : `${ordinal(i + 1)} after`}
                </Typography>
                <NumberField value={gap} min={1} label={`${g.label}: ${ordinal(i + 1)} reminder, days`} onChange={(n) => patchStep(g.key, i, n)} />
                <Typography variant="bodySmall">{i === 0 ? g.firstUnit : "more days"}</Typography>
                {v.steps.length > 1 && (
                  <IconButton
                    aria-label={`Remove the ${ordinal(i + 1)} reminder of ${g.label}`}
                    onClick={() => patchGroup(g.key, { steps: v.steps.filter((_, j) => j !== i) })}
                  >
                    <RiDeleteBinLine size={18} />
                  </IconButton>
                )}
              </Line>
            ))}
            {v.steps.length < LIMITS.steps && (
              <Button
                startIcon={<RiAddLine size={18} />}
                onClick={() => patchGroup(g.key, { steps: [...v.steps, v.steps[v.steps.length - 1] || 7] })}
                sx={{ alignSelf: "flex-start" }}
              >
                Add a reminder
              </Button>
            )}
            <Line>
              <Typography variant="bodySmall" sx={{ width: 150 }}>
                Then every
              </Typography>
              <NumberField value={v.thenEvery} min={0} label={`${g.label}: then every, days`} onChange={(n) => patchGroup(g.key, { thenEvery: n })} />
              <Typography variant="bodySmall">days (0 = stop)</Typography>
            </Line>
            <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
              {dayList(v.steps, v.thenEvery)}
            </Typography>
            {g.messages.map((m) => {
              const msg = form.messages[m.key];
              const label = MESSAGE_LABELS[m.key];
              return (
                <Box key={m.key} sx={{ display: "flex", flexDirection: "column", gap: "8px", pt: "6px" }}>
                  <Typography variant="bodySmallStrong">{m.label}</Typography>
                  <FieldLabel id={`br-${m.key}-title`} label="Title" counter={`${msg.title.length} / ${LIMITS.title}`} />
                  <Field
                    id={`br-${m.key}-title`}
                    value={msg.title}
                    onChange={(e) => patchMessage(m.key, { title: e.target.value })}
                    inputProps={{ "aria-label": `${label}: title` }}
                  />
                  <FieldLabel id={`br-${m.key}-body`} label="Text" counter={`${msg.body.length} / ${LIMITS.body}`} />
                  <Field
                    id={`br-${m.key}-body`}
                    multiline
                    minRows={2}
                    value={msg.body}
                    onChange={(e) => patchMessage(m.key, { body: e.target.value })}
                    inputProps={{ "aria-label": `${label}: text` }}
                  />
                </Box>
              );
            })}
          </Card>
        );
      })}

      <Card>
        <SectionHead title="Send test" subtitle="Push one message, as it is on this page, to a team test account. No card is added." />
        {loaded.testAccounts.length === 0 ? (
          <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
            No team test account has a device yet.
          </Typography>
        ) : (
          <Line>
            <TextField
              select
              size="small"
              id="br-test-account"
              label="Account"
              value={test.uid}
              onChange={(e) => setTest((t) => ({ ...t, uid: e.target.value }))}
              slotProps={{ select: { native: true } }}
            >
              {loaded.testAccounts.map((a) => (
                <option key={a.uid} value={a.uid}>
                  {a.name}
                </option>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              id="br-test-message"
              label="Message"
              value={test.key}
              onChange={(e) => setTest((t) => ({ ...t, key: e.target.value }))}
              slotProps={{ select: { native: true } }}
            >
              {MESSAGE_KEYS.map((k) => (
                <option key={k} value={k}>
                  {MESSAGE_LABELS[k]}
                </option>
              ))}
            </TextField>
            <Button variant="outlined" startIcon={<RiSendPlane2Fill size={18} />} disabled={test.sending || !test.uid} onClick={sendTest}>
              {test.sending ? "Sending…" : "Send test"}
            </Button>
          </Line>
        )}
        {test.error && <Alert severity="error">{test.error}</Alert>}
      </Card>

      <Card>
        <SectionHead title="Last run" subtitle={run ? run.heading : `No run yet. The next is at ${hourLabel(loaded.settings.hour)} UK time.`} />
        {run && (
          <Table size="small" aria-label="Last run">
            <TableHead>
              <TableRow>
                <TableCell>Message</TableCell>
                <TableCell align="right">{run.countLabel}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {run.rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell align="right">{r.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {run && run.skips.length > 0 && (
          <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
            Skipped: {run.skips.map((s) => `${s.label} ${s.count}`).join(" · ")}
          </Typography>
        )}
      </Card>

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "12px",
          py: "12px",
          bgcolor: tokens.surface.default,
          borderTop: `1px solid ${tokens.border.default}`,
        }}
      >
        {(saveError || problem) && (
          <Typography role="alert" variant="bodySmall" sx={{ color: tokens.error.ink, flex: 1 }}>
            {saveError || problem}
          </Typography>
        )}
        <Button variant="contained" size="large" disabled={!dirty || !!problem || saving} onClick={onSave}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </Box>

      <Dialog open={confirmLive} onClose={() => setConfirmLive(false)}>
        <DialogTitle>{startsSending ? "Start sending reminders?" : "Save the new schedule?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            At {hourLabel(form.hour)} UK time, everyone already past a reminder's day gets it, all at once.{lastLine}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLive(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => {
              setConfirmLive(false);
              save();
            }}
          >
            {startsSending ? "Start sending" : "Save schedule"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast("")} message={toast} />
    </Box>
  );
};

export default BookingReminders;
